import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, Subject, throwError } from 'rxjs';
import { switchMap, catchError, take } from 'rxjs/operators';
import { AuthService } from '../auth';

const OAUTH2_TOKENS_URL = '/oauth2/tokens';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new Subject<any>();

  constructor(
    private authService: AuthService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(error => {

        // Ignore refresh requests themselves
        if (request.url.includes(OAUTH2_TOKENS_URL)) {
          return throwError(error);
        }

        // Ignore non-401 errors
        if (error.status !== 401) {
          return throwError(error);
        }

        // Queue retries while token is being refreshed
        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject.pipe(
              take(1),
              switchMap((refreshSuccess) => {
                if (refreshSuccess) {
                  // Refire refreshed request
                  console.log('Retrying API call...');
                  return next.handle(this.refreshRequest(request));
                }

                // Re-throw original error
                return throwError(error);
              }));
        }

        console.log('Attempting OAuth2 tokens refresh...');
        this.refreshTokenInProgress = true;

        return from(this.authService.renewTokens()).pipe(
            switchMap(() => {
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(true);

            console.log('Retrying API call...');
            // Refire refreshed request
            return next.handle(this.refreshRequest(request));
          }),
          catchError((err: any) => {
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(false);

            console.error('Failed to refresh access token', err);

            return throwError(err);
          })
        );
    }));
  }

  private refreshRequest(request: HttpRequest<any>) {
    const accessToken = this.authService.getAccessToken();
    // Clone immutable request with new access token
    return request.clone({
      setHeaders: {
          'Authorization': `Bearer ${accessToken}`,
      }
    });
  }
}
